// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import produce from 'immer';
import {
  AdvisoryBankProductType,
  type AdvisoryInstitution,
} from '@shared/data-access/types/brokerage-types';

export type IlliquidLinkableProduct = {
  iban: string;
  name: string;
  product_type: AdvisoryBankProductType;
  type?: string | null;
  institution_id: string;
  institution_name: string;
  logo_url?: string | null;
};

const flattenInstitutionProducts = (institution: AdvisoryInstitution) =>
  [
    ...institution.products.accounts.map(product => ({
      ...product,
      product_type: AdvisoryBankProductType.Account as const,
    })),
    ...institution.products.cards.map(product => ({
      ...product,
      product_type: AdvisoryBankProductType.Card as const,
    })),
    ...institution.products.loans.map(product => ({
      ...product,
      product_type: AdvisoryBankProductType.Loan as const,
    })),
    ...institution.products.portfolios.map(product => ({
      ...product,
      product_type: AdvisoryBankProductType.Portfolio as const,
    })),
  ].map(product => ({
    iban: product.iban,
    name: product.name ?? institution.name,
    product_type: product.product_type,
    type: product.type,
    institution_id: institution.id,
    institution_name: institution.name,
    logo_url: institution.logo_url,
  }));

/** First occurrence wins per distinct IBAN. */
export const buildLinkableProductsByIban = (
  institutions: AdvisoryInstitution[],
): Map<string, IlliquidLinkableProduct> =>
  institutions
    .flatMap(flattenInstitutionProducts)
    .filter(product => Boolean(product.iban))
    .reduce(
      (acc, product) =>
        produce(acc, draft => {
          if (!draft.has(product.iban)) {
            draft.set(product.iban, product);
          }
        }),
      new Map<string, IlliquidLinkableProduct>(),
    );

export const resolveLinkedProducts = (
  ibans: readonly string[],
  byIban: Map<string, IlliquidLinkableProduct>,
): IlliquidLinkableProduct[] =>
  ibans.map(
    iban =>
      byIban.get(iban) ?? {
        iban,
        name: iban,
        product_type: AdvisoryBankProductType.Account,
        institution_id: '',
        institution_name: '-',
        logo_url: null,
      },
  );
